/**
 * ToolSteps — per-tool execution status row(s) inside an AI message.
 *
 * Ported from mobile Flutter `lib/widgets/chat/tool_steps.dart` so web has
 * the same animation language. State source: ai_stream SSE `tool_start` /
 * `tool_result` frames accumulated in `state.toolsUsed` by streamingHandler.
 *
 * Behaviour:
 *   - any running   → show active tool's running-label with shimmer + icon
 *   - all done, 1   → static "Searched the web" label + icon
 *   - all done, >1  → collapsed "N steps" — click expands a list
 *   - hidden plumbing tools (read_visual_spec / read_circuit_spec) are
 *     suppressed; when one is running with no visible tool yet, fall back
 *     to a rotating spinner-verb row (matches mobile UX)
 */
import React, { useMemo, useState, useEffect } from 'react';
import {
  FaGlobe, FaYoutube, FaLink, FaGraduationCap, FaFileAlt, FaCloud,
  FaImage, FaPaintBrush, FaProjectDiagram, FaTable, FaPuzzlePiece,
  FaMicrochip, FaQuestionCircle, FaCamera, FaShieldAlt, FaClipboardCheck,
  FaFolderOpen, FaFileImport, FaStickyNote, FaFile, FaHeartbeat, FaSearch,
  FaCalendarAlt, FaCalendarPlus, FaListUl, FaPlusCircle, FaBrain,
  FaUniversity, FaBuilding, FaWrench, FaChevronRight, FaExternalLinkAlt,
} from 'react-icons/fa';
import { SPINNER_VERBS } from '../constants/spinnerVerbs';
import '../styles/ToolSteps.css';

const RUNNING_LABEL = {
  web_search: 'Searching the web',
  youtube_search: 'Searching YouTube',
  fetch_url: 'Fetching webpage',
  academic_search: 'Searching papers',
  fetch_paper_content: 'Reading paper',
  get_weather: 'Checking weather',
  generate_image: 'Generating image',
  edit_image: 'Editing image',
  create_diagram: 'Creating diagram',
  create_spreadsheet: 'Creating spreadsheet',
  read_visual_spec: 'Loading visual spec',
  show_widget: 'Rendering widget',
  read_circuit_spec: 'Loading circuit spec',
  show_circuit: 'Rendering circuit',
  ask_user_input: 'Asking for input',
  screenshot: 'Capturing screen',
  search_safety_standards: 'Searching IEC standards',
  search_compliance_standards: 'Searching compliance',
  get_project_files: 'Listing project files',
  view_file: 'Viewing file',
  save_project_notes: 'Saving project notes',
  create_file: 'Creating file',
  get_safety_content: 'Browsing safety content',
  keyword_search_safety: 'Searching safety docs',
  read_calendar: 'Checking calendar',
  create_calendar_event: 'Creating calendar event',
  read_reminders: 'Checking reminders',
  create_reminder: 'Creating reminder',
  save_memory: 'Saving to memory',
  search_finance_events: 'Searching finance events',
  search_realestate_events: 'Searching property events',
};

const DONE_LABEL = {
  web_search: 'Searched the web',
  youtube_search: 'Searched YouTube',
  fetch_url: 'Fetched webpage',
  academic_search: 'Searched papers',
  fetch_paper_content: 'Reading paper',
  get_weather: 'Checked weather',
  generate_image: 'Generated image',
  edit_image: 'Edited image',
  create_diagram: 'Created diagram',
  create_spreadsheet: 'Created spreadsheet',
  read_visual_spec: 'Loading visual spec',
  show_widget: 'Rendered widget',
  read_circuit_spec: 'Loading circuit spec',
  show_circuit: 'Rendered circuit',
  ask_user_input: 'Asked for input',
  screenshot: 'Captured screen',
  search_safety_standards: 'Searched IEC standards',
  search_compliance_standards: 'Searched compliance',
  get_project_files: 'Listed project files',
  view_file: 'Viewing file',
  save_project_notes: 'Saved project notes',
  create_file: 'Created file',
  get_safety_content: 'Browsed safety content',
  keyword_search_safety: 'Searched safety docs',
  read_calendar: 'Checked calendar',
  create_calendar_event: 'Created calendar event',
  read_reminders: 'Checked reminders',
  create_reminder: 'Created reminder',
  save_memory: 'Updated memory',
  search_finance_events: 'Searched finance events',
  search_realestate_events: 'Searched property events',
};

const ICON = {
  web_search: FaGlobe,
  youtube_search: FaYoutube,
  fetch_url: FaLink,
  academic_search: FaGraduationCap,
  fetch_paper_content: FaFileAlt,
  get_weather: FaCloud,
  generate_image: FaImage,
  edit_image: FaPaintBrush,
  create_diagram: FaProjectDiagram,
  create_spreadsheet: FaTable,
  show_widget: FaPuzzlePiece,
  show_circuit: FaMicrochip,
  ask_user_input: FaQuestionCircle,
  screenshot: FaCamera,
  search_safety_standards: FaShieldAlt,
  search_compliance_standards: FaClipboardCheck,
  get_project_files: FaFolderOpen,
  view_file: FaFileImport,
  save_project_notes: FaStickyNote,
  create_file: FaFile,
  get_safety_content: FaHeartbeat,
  keyword_search_safety: FaSearch,
  read_calendar: FaCalendarAlt,
  create_calendar_event: FaCalendarPlus,
  read_reminders: FaListUl,
  create_reminder: FaPlusCircle,
  save_memory: FaBrain,
  search_finance_events: FaUniversity,
  search_realestate_events: FaBuilding,
};

// Internal plumbing tools — never surface as user-visible rows. When one is
// running before any action tool is visible, show a rotating spinner-verb.
const HIDDEN = new Set(['read_visual_spec', 'read_circuit_spec']);

const SEARCH_TOOLS_USE_QUERY = new Set([
  'web_search', 'youtube_search', 'academic_search',
  'search_finance_events', 'search_realestate_events',
]);

// AU broadcast-event source enum → display label. Ported from mobile
// tool_steps.dart `_sourceLabels` so event rows read the same on web.
const SOURCE_LABELS = {
  asx: 'ASX', asic: 'ASIC', apra: 'APRA', rba: 'RBA', ato: 'ATO',
  treasury: 'Treasury', abs: 'ABS', aemo: 'AEMO',
  nsw_gazette: 'NSW Gazette', nsw_valuer_general: 'NSW Valuer General',
  bom: 'BoM',
};

function getLabel(tool) {
  if (!tool || typeof tool !== 'object') return String(tool || '');
  const name = tool.name || '';
  const running = tool.running === true;
  const query = tool.query || (tool.arguments && tool.arguments.query) || '';
  if (query && SEARCH_TOOLS_USE_QUERY.has(name)) return query;
  if (running) return RUNNING_LABEL[name] || name.replace(/_/g, ' ');
  return DONE_LABEL[name] || name.replace(/_/g, ' ');
}

function getIcon(tool) {
  return ICON[tool && tool.name] || FaWrench;
}

function hasSources(tool) {
  return !!(tool && Array.isArray(tool.sources) && tool.sources.length);
}

// Hostname is decorative — scraped result URLs are external, untrusted data,
// so a malformed or empty URL simply yields no host line. There is nothing
// for the user to act on in that case, so no error needs surfacing.
function hostOf(url) {
  try { return new URL(url).hostname.replace(/^www\./, ''); }
  catch (e) { return ''; }
}

// lodged_at arrives as an ISO string on AU-event sources. If it is not a
// parseable date, show it verbatim — it is backend-supplied display text
// either way, and an event row without a date is still useful.
function formatLodged(iso) {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso ? String(iso) : '';
  const m = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][d.getMonth()];
  return `${m} ${d.getDate()}, ${d.getFullYear()}`;
}

function authorsText(a) {
  if (Array.isArray(a)) return a.join(', ');
  return a ? String(a) : '';
}

// Secondary line under a source title, shaped by which fields the backend
// shipped: youtube → channel/views, academic → authors/year/citations,
// AU event → source/ticker/region/date, plain web → hostname.
function sourceMeta(s) {
  if (s.channel || s.views) {
    return [s.channel, s.views].filter(Boolean).join(' · ');
  }
  if (s.authors || s.year) {
    const bits = [];
    const au = authorsText(s.authors);
    if (au) bits.push(au);
    if (s.year) bits.push(String(s.year));
    if (s.citations) bits.push(`${s.citations} citations`);
    return bits.join(' · ');
  }
  if (s.source || s.lodged_at) {
    const bits = [];
    if (s.source) bits.push(SOURCE_LABELS[s.source] || String(s.source).toUpperCase());
    if (s.ticker) bits.push(s.ticker);
    if (s.region) bits.push(s.region);
    if (s.lodged_at) bits.push(formatLodged(s.lodged_at));
    return bits.join(' · ');
  }
  return hostOf(s.url);
}

// One reference row inside an expanded tool. Clickable when the source has a
// URL — opens the origin in a new tab so the user can verify it directly.
function SourceRow({ s }) {
  if (!s || typeof s !== 'object') return null;
  const url = s.url ? String(s.url) : '';
  const title = s.title || url || 'Untitled source';
  const meta = sourceMeta(s);
  const snippet = s.snippet ? String(s.snippet) : '';

  const body = (
    <>
      <div className="tool-steps-source-title">{title}</div>
      {meta && <div className="tool-steps-source-meta">{meta}</div>}
      {snippet && <div className="tool-steps-source-snippet">{snippet}</div>}
    </>
  );

  if (!url) return <div className="tool-steps-source">{body}</div>;
  return (
    <a
      className="tool-steps-source tool-steps-source-link"
      href={url}
      target="_blank"
      rel="noopener noreferrer"
    >
      {body}
      <FaExternalLinkAlt className="tool-steps-source-ext" />
    </a>
  );
}

// One tool inside the expanded panel: a step header, plus its reference list
// when the tool returned sources (web_search, academic_search, events, …).
// Tools with no sources (get_weather, save_memory, …) render the header only.
function ToolDetail({ tool }) {
  const Icon = getIcon(tool);
  const isError = tool && tool.ok === false;
  const errMsg = isError ? (tool.error_message || tool.message) : '';
  const sources = hasSources(tool) ? tool.sources : [];
  return (
    <div className="tool-steps-detail">
      <div className={`tool-steps-row tool-steps-sub ${isError ? 'tool-steps-error' : ''}`}>
        <Icon className="tool-steps-icon" />
        <span className="tool-steps-label-wrap"><span>{getLabel(tool)}</span></span>
      </div>
      {errMsg && <div className="tool-steps-error-msg">— {errMsg}</div>}
      {sources.length > 0 && (
        <div className="tool-steps-source-list">
          {sources.map((s, i) => <SourceRow key={i} s={s} />)}
        </div>
      )}
    </div>
  );
}

function SpinnerVerbRow() {
  const [idx, setIdx] = useState(() => Math.floor(Math.random() * SPINNER_VERBS.length));
  useEffect(() => {
    const t = setInterval(() => {
      setIdx((cur) => {
        let next = Math.floor(Math.random() * SPINNER_VERBS.length);
        if (next === cur && SPINNER_VERBS.length > 1) next = (next + 1) % SPINNER_VERBS.length;
        return next;
      });
    }, 1800);
    return () => clearInterval(t);
  }, []);
  return (
    <div className="tool-steps-row tool-steps-running">
      <FaWrench className="tool-steps-icon" />
      <span className="tool-steps-label-wrap">
        <span className="tool-steps-shimmer">{SPINNER_VERBS[idx]}…</span>
      </span>
    </div>
  );
}

const ToolSteps = ({ tools }) => {
  const [expanded, setExpanded] = useState(false);

  // Sort by `turn` ascending, stable on insertion order. Hidden plumbing
  // tools are excluded from the visible list but tracked for the spinner
  // fallback. Same logic as mobile `_sortedTools`.
  const { visible, hiddenRunning } = useMemo(() => {
    if (!Array.isArray(tools)) return { visible: [], hiddenRunning: false };
    const indexed = [];
    let hidden = false;
    for (let i = 0; i < tools.length; i++) {
      const t = tools[i];
      if (t && typeof t === 'object' && HIDDEN.has(t.name)) {
        if (t.running === true) hidden = true;
        continue;
      }
      indexed.push({ i, t });
    }
    indexed.sort((a, b) => {
      const ta = (a.t && a.t.turn) || 0;
      const tb = (b.t && b.t.turn) || 0;
      if (ta !== tb) return ta - tb;
      return a.i - b.i;
    });
    return { visible: indexed.map((x) => x.t), hiddenRunning: hidden };
  }, [tools]);

  if (!Array.isArray(tools) || (visible.length === 0 && !hiddenRunning)) return null;
  if (visible.length === 0 && hiddenRunning) return <SpinnerVerbRow />;

  const activeRunning = visible.find((t) => t && t.running === true);
  const anyRunning = !!activeRunning;
  const firstTool = activeRunning || visible[0];
  const isMultiple = visible.length > 1;
  const Icon = getIcon(firstTool);

  // Collapsed header: while running show the active tool's label; when done
  // with multiple tools, show "N steps". A single done tool that returned
  // sources is expandable too, so the user can open its reference list.
  const headerLabel = anyRunning
    ? getLabel(firstTool)
    : (isMultiple ? `${visible.length} steps` : getLabel(firstTool));

  const canExpand = !anyRunning && (isMultiple || hasSources(firstTool));

  // Key the label wrapper on the active tool so a tool handoff during live
  // streaming remounts it — replaying the fade-in transition while the
  // shimmer (its own infinite animation) keeps sweeping uninterrupted.
  const labelKey = firstTool
    ? (firstTool.tool_call_id || `${firstTool.name}:${firstTool.turn || 0}`)
    : headerLabel;

  return (
    <div className="tool-steps">
      <div
        className={`tool-steps-row ${anyRunning ? 'tool-steps-running' : ''} ${canExpand ? 'tool-steps-clickable' : ''}`}
        onClick={canExpand ? () => setExpanded((e) => !e) : undefined}
        role={canExpand ? 'button' : undefined}
        tabIndex={canExpand ? 0 : undefined}
      >
        <Icon className="tool-steps-icon" />
        <span className="tool-steps-label-wrap" key={labelKey}>
          <span className={anyRunning ? 'tool-steps-shimmer' : ''}>{headerLabel}</span>
        </span>
        {canExpand && (
          <FaChevronRight
            className="tool-steps-chevron"
            style={{ transform: expanded ? 'rotate(90deg)' : 'none' }}
          />
        )}
      </div>

      {expanded && canExpand && (
        <div className="tool-steps-expanded">
          {visible.map((t, i) => <ToolDetail key={i} tool={t} />)}
        </div>
      )}
    </div>
  );
};

export default ToolSteps;
